import React, { useEffect } from "react";
import { useFonePay } from "../hooks/useFonePay";
import { useNavigate } from "react-router-dom";
import { Paths } from "../constants/Paths";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Smartphone, QrCode, CheckCircle2, Loader2, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDispatch } from "react-redux";
import { updateBookingAfterPayment } from "../actions/tour/tourAction";

export default function FonepayPayment({ rs, pricing }) {
  // keep title behavior
  document.title = "Fonepay Payment";

  const { status, verifiedRs, paySuccessRs } = useFonePay(rs.Result);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (paySuccessRs) {
      console.log("Payment success rs", paySuccessRs);
      window.location.href = rs.SuccessURL + `?deviceid=${paySuccessRs?.deviceId ?? ""}&oid=${rs.CheckoutID}`;
    }
  }, [paySuccessRs, rs.SuccessURL, rs.CheckoutID]);

  const qrAvailable = !!rs?.Result?.QRImage;
  const isScanned = !!verifiedRs;
  const isPaid = !!paySuccessRs;

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <Card className="max-w-md w-full border-none shadow-2xl relative overflow-hidden">
        {/* Animated Background Accent */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full -mr-16 -mt-16 blur-2xl" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary/5 rounded-full -ml-16 -mb-16 blur-2xl" />

        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 bg-primary/10 w-16 h-16 rounded-2xl flex items-center justify-center">
            <Smartphone className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight">Fonepay — Scan & Pay</CardTitle>
          <CardDescription className="text-base font-medium">
            Scan the QR using Fonepay app to complete payment
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-8 pt-6">
          {/* QR Code Section */}
          <div className="relative group mx-auto w-fit">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-primary/10 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative bg-white p-4 rounded-xl shadow-lg ring-1 ring-slate-200 dark:ring-slate-800">
              {qrAvailable ? (
                <div className="relative w-64 h-64 mx-auto overflow-hidden rounded-lg bg-white">
                  <img src={rs.Result.QRImage} alt="Fonepay QR" className="w-full h-full object-contain" />

                  {/* Scanning Animation */}
                  {!isPaid && (
                    <div className="absolute top-0 left-0 w-full h-1 bg-primary/60 shadow-[0_0_15px_rgba(var(--primary),0.8)] animate-[scan_3s_linear_infinite]"
                      style={{
                        animationName: 'scan',
                        animationDuration: '3s',
                        animationTimingFunction: 'linear',
                        animationIterationCount: 'infinite'
                      }}>
                    </div>
                  )}

                  {/* Success Overlay */}
                  {isPaid && (
                    <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center animate-in fade-in zoom-in duration-500">
                      <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                        <CheckCircle2 className="w-12 h-12" />
                      </div>
                      <span className="text-green-700 font-bold uppercase tracking-wider">Payment Received</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-64 h-64 flex flex-col items-center justify-center space-y-2 text-muted-foreground bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <QrCode className="w-12 h-12 opacity-20" />
                  <span className="text-sm font-medium">QR Image Not Available</span>
                </div>
              )}
            </div>
          </div>

          <style dangerouslySetInnerHTML={{
            __html: `
            @keyframes scan {
              0%, 100% { transform: translateY(0); opacity: 0; }
              5% { opacity: 1; }
              95% { opacity: 1; }
              100% { transform: translateY(256px); opacity: 0; }
            }
          `}} />

          {/* Pricing & Status */}
          <div className="space-y-4 text-center">
            {pricing && (
              <div className="flex flex-col items-center justify-center">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">Payable Amount</span>
                <span className="text-4xl font-extrabold text-slate-900 dark:text-slate-100 italic">
                  <span className="text-sm align-top mr-1 font-medium italic opacity-60 uppercase">{pricing.Currency}</span>
                  {Number(pricing.GTotalFare).toLocaleString()}
                </span>
              </div>
            )}

            <div className="flex items-center justify-center pt-2">
              <Badge
                variant={isPaid ? "success" : isScanned ? "secondary" : "outline"}
                className={cn(
                  "px-4 py-1.5 text-sm font-semibold rounded-full gap-2 transition-all duration-300",
                  !isPaid && !isScanned && "bg-amber-50 text-amber-700 border-amber-200 animate-pulse",
                  isScanned && "bg-blue-50 text-blue-700 border-blue-200",
                  isPaid && "bg-green-50 text-green-700 border-green-200"
                )}
              >
                {!isPaid && !isScanned && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {isScanned && !isPaid && <QrCode className="w-3.5 h-3.5" />}
                {isPaid && <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />}
                {isPaid ? "Payment Successful" : isScanned ? "QR Scanned, Awaiting Payment" : "Awaiting scan..."}
              </Badge>
            </div>
          </div>

          {/* Instruction Steps */}
          {!isPaid && (
            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2 mb-4 text-xs font-bold text-slate-400 uppercase tracking-widest">
                <Info className="w-3.5 h-3.5" />
                Quick Steps
              </div>
              <ul className="grid grid-cols-1 gap-3">
                <li className="flex items-start gap-3 text-sm">
                  <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</div>
                  <p className="text-slate-600 dark:text-slate-400">Open your mobile banking or <span className="font-bold text-primary">Fonepay</span> app.</p>
                </li>
                <li className="flex items-start gap-3 text-sm">
                  <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</div>
                  <p className="text-slate-600 dark:text-slate-400 font-medium">Select <span className="font-bold text-slate-800 dark:text-slate-200 italic">Scan & Pay</span> and point to the QR code.</p>
                </li>
              </ul>
              <p className="mt-6 text-[11px] text-center text-slate-400 italic font-medium">
                Do not close this window. You will be redirected automatically upon success.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
